import { Icon } from '@iconify/react';
import {
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Menu,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Cake as CakeIcon, Person as PersonIcon } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { identifierByConfig } from 'src/utils/fhir';
import uuidv4 from 'src/utils/uuidv4';
import MRNIcon from 'src/assets/icons/mrn.svg';
import moment from 'moment';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import Iconify from 'src/components/Iconify';
import { useState } from 'react';

type Props = {
  row: any;
  translate: any;
  menuItems: JSX.Element;
};

export default function PatientRow({ row, translate, menuItems }: Props) {
  const [openMenu, setOpenMenu] = useState<HTMLElement | null>(null);

  const {
    indexedMrn,
    indexedIdentifierJson,
    indexedFullName,
    indexedAge,
    indexedDOBFormatted,
    indexedGender,
    indexedPhone,
    indexedEmail,
    indexedStatus,
    indexedCustomEpisodeOfCare,
    indexedCreatedAtFormatted,
    metaLastUpdated,
    indexedManagingOrgDisplay,
  } = row;

  const { componentsData } = useTenantConfigData();
  const { list } = componentsData?.patient ?? {};

  const identifiersMap = identifierByConfig(indexedIdentifierJson, list);

  const lastUpdated = metaLastUpdated ? moment(metaLastUpdated).format('MMM DD, YYYY') : null;

  return (
    <>
      <TableRow>
        <TableCell>
          <Tooltip title={translate('patients.mrn')}>
            <Typography variant="subtitle2">
              <MRNIcon style={{ height: 13, width: 13, marginRight: 9 }} />
              {indexedMrn}
            </Typography>
          </Tooltip>
          {identifiersMap?.map((i) => (
            <Tooltip title={i?.tooltip} key={uuidv4()}>
              <Typography variant="body2" display="flex" alignItems="center">
                <Icon icon={i?.icon} style={{ ...i?.properties }} />
                {i?.value ? i?.value : null}
              </Typography>
            </Tooltip>
          ))}
        </TableCell>
        <TableCell>
          <Typography variant="subtitle2">
            <PersonIcon sx={{ marginRight: 1, marginBottom: '-2px' }} fontSize={'small'} />{' '}
            {indexedFullName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <CakeIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
              fontSize={'small'}
            />
            {indexedDOBFormatted ? `${indexedDOBFormatted} (${indexedAge})` : null}
          </Typography>
          <Typography variant="body2">
            {indexedGender && `${indexedGender?.charAt(0).toUpperCase()}${indexedGender?.slice(1)}`}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            <PhoneIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-2px' }}
              fontSize={'small'}
            />
            {indexedPhone}
          </Typography>
          <Typography variant="body2">
            <EmailIcon
              sx={{ marginRight: 1, color: '#919eab', marginBottom: '-5px' }}
              fontSize={'small'}
            />
            {indexedEmail}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={indexedStatus}
            color={(indexedStatus === 'Active' && 'success') || 'error'}
            sx={{
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        </TableCell>
        <TableCell>
          <Grid container direction="column" spacing={1}>
            {indexedCustomEpisodeOfCare &&
              Object.keys(indexedCustomEpisodeOfCare).map((eocId) => {
                const eoc = indexedCustomEpisodeOfCare[eocId];
                const title = eoc.type[0].text;
                let firstTypeCodingDisplay: string | undefined = undefined;
                eoc.type.find((item) => {
                  const firstCodingDisplay = item.coding.find((cod) => cod.display);

                  if (firstCodingDisplay) {
                    firstTypeCodingDisplay = firstCodingDisplay.display;
                  }

                  return !!firstCodingDisplay;
                });
                let label = '';

                if (title) {
                  label += title + ' : ';
                } else if (firstTypeCodingDisplay) {
                  label += firstTypeCodingDisplay + ' : ';
                } else {
                  label += '- : ';
                }

                label += eoc.status;

                return (
                  <Grid key={eocId} item xs={12}>
                    <Chip key={eocId} size="small" label={label} color={'info'} />
                  </Grid>
                );
              })}
          </Grid>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{indexedManagingOrgDisplay}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            Registration Date: {indexedCreatedAtFormatted ? indexedCreatedAtFormatted : null}
          </Typography>
          <Typography variant="body2">Last Updated: {lastUpdated ? lastUpdated : '-'}</Typography>
        </TableCell>
        <TableCell align="center">
          <>
            <IconButton
              onClick={(event) => setOpenMenu(event.currentTarget)}
              sx={{
                color: '#637381',
                '&:hover': {
                  backgroundColor: 'rgba(99, 115, 129, 0.08)',
                },
              }}
            >
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>

            <Menu
              anchorEl={openMenu}
              open={Boolean(openMenu)}
              onClose={() => setOpenMenu(null)}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {menuItems ? menuItems : null}
            </Menu>
          </>
        </TableCell>
      </TableRow>
    </>
  );
}
